import { useEffect, useRef, useState } from "react";
import { MessagesSquare, X } from "lucide-react";
import { trackChatOpen } from "@/lib/analytics";

/**
 * Custom launcher for Tawk.to chat — B&B Emmanuel website.
 *
 * Differs from the booking-guide-plus app:
 *  - shows a friendly "pre-chat" balloon (welcome message) above the button,
 *    appearing 4s after page load, dismissable, only once per session.
 *  - identifies the visitor as coming from the public website (not a guest
 *    with check-in code) via Tawk_API.setAttributes.
 *
 * The Tawk default minimized bubble + "We Are Here!" balloon are hidden by
 * CSS in public/index.html until body has data-tawk-open="1".
 */
const WELCOME_KEY = "bb_chat_welcome_dismissed";

const TawkChatButton = () => {
  const [ready, setReady] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const welcomeTimer = useRef(null);

  useEffect(() => {
    // Wait for Tawk_API and tag this visitor as "website"
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      if (window.Tawk_API && typeof window.Tawk_API.maximize === "function") {
        setReady(true);
        try {
          if (typeof window.Tawk_API.setAttributes === "function") {
            window.Tawk_API.setAttributes(
              {
                source: "Sito B&B Emmanuel",
                visitor_type: "Visitatore sito web (non ancora ospite)",
                channel: "website",
              },
              () => {}
            );
          }
        } catch (e) {
          // ignore
        }
        clearInterval(interval);
      }
      if (attempts > 60) clearInterval(interval);
    }, 500);

    // Show the welcome balloon 4s after mount, only once per session
    if (!sessionStorage.getItem(WELCOME_KEY)) {
      welcomeTimer.current = setTimeout(() => setShowWelcome(true), 4000);
    }

    return () => {
      clearInterval(interval);
      if (welcomeTimer.current) clearTimeout(welcomeTimer.current);
    };
  }, []);

  const dismissWelcome = () => {
    setShowWelcome(false);
    try {
      sessionStorage.setItem(WELCOME_KEY, "1");
    } catch (e) { /* ignore */ }
  };

  const openChat = () => {
    dismissWelcome();
    trackChatOpen();
    if (!window.Tawk_API || typeof window.Tawk_API.maximize !== "function") {
      // eslint-disable-next-line no-console
      console.warn("Tawk_API not ready yet");
      return;
    }
    document.body.setAttribute("data-tawk-open", "1");
    if (typeof window.Tawk_API.showWidget === "function") {
      window.Tawk_API.showWidget();
    }
    window.Tawk_API.maximize();
  };

  return (
    <>
      {/* Welcome balloon — appears 4s after load, before user clicks the bubble */}
      {showWelcome && (
        <div
          data-testid="chat-welcome-balloon"
          className="fixed bottom-44 right-24 z-[9999] max-w-[260px] bg-white border border-[#E8E2D9] rounded-2xl shadow-2xl p-4 animate-in fade-in slide-in-from-bottom-2 duration-300"
          style={{ animation: "fadeInUp 0.4s ease-out" }}
        >
          <button
            onClick={dismissWelcome}
            aria-label="Chiudi"
            data-testid="chat-welcome-close"
            className="absolute top-2 right-2 w-6 h-6 rounded-full hover:bg-[#F5F1EA] flex items-center justify-center text-[#635F59]"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <div className="flex items-start gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[#AC5D45] text-white flex items-center justify-center shrink-0 mt-0.5">
              <MessagesSquare className="w-4 h-4" />
            </div>
            <div>
              <p className="font-semibold text-[#1E3A5F] text-sm leading-tight">
                Ciao! 👋
              </p>
              <p className="text-[10px] uppercase tracking-wider text-[#AC5D45]">
                B&B Emmanuel
              </p>
            </div>
          </div>
          <p className="text-sm text-[#2D2926] leading-snug mb-3">
            Sono l'<strong>assistente del B&B Emmanuel</strong>.
            Hai domande sulle camere, sui prezzi o sulla zona?
            Chiedimi pure — siamo qui per te 😊
          </p>
          <button
            onClick={openChat}
            data-testid="chat-welcome-open"
            className="w-full bg-[#AC5D45] hover:bg-[#8A4936] text-white text-sm font-semibold py-2 rounded-lg transition"
          >
            Scrivici ora
          </button>
          {/* Tail pointing at the chat button */}
          <div
            className="absolute -right-2 bottom-6 w-0 h-0"
            style={{
              borderTop: "8px solid transparent",
              borderBottom: "8px solid transparent",
              borderLeft: "10px solid white",
            }}
          />
        </div>
      )}

      <button
        onClick={openChat}
        data-testid="tawk-chat-launcher"
        aria-label="Apri chat"
        title={ready ? "Scrivici" : "Caricamento chat..."}
        className="fixed bottom-44 right-5 z-[9999] w-16 h-16 rounded-full bg-[#AC5D45] hover:bg-[#8A4936] text-white shadow-2xl flex items-center justify-center transition-all hover:scale-105"
      >
        <MessagesSquare className="w-7 h-7" />
        {showWelcome && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
            1
          </span>
        )}
      </button>
    </>
  );
};

export default TawkChatButton;
